import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { userService } from '../services/api';
import { Spinner } from './Spinner';
import {
  ChevronRightIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  ListBulletIcon,
} from '@heroicons/react/24/outline';

const ROLE_LABELS = { super_admin: 'Super Admin', admin: 'Admin', dealer: 'Dealer', sales_executive: 'Sales Executive' };
const ROLE_PLURAL = { admin: 'admins', dealer: 'dealers', sales_executive: 'sales executives' };
const ROLE_BADGE  = {
  super_admin:     'badge-danger',
  admin:           'badge-warning',
  dealer:          'badge-primary',
  sales_executive: 'badge-secondary',
};
const ROLE_RANK = { super_admin: 0, admin: 1, dealer: 2, sales_executive: 3 };

const fullName = (u) => `${u.firstName || ''} ${u.lastName || ''}`.trim();
const initials = (u) => `${u.firstName?.[0] || ''}${u.lastName?.[0] || ''}`.toUpperCase() || '?';
const parentOf = (u) => String((typeof u.parentId === 'object' ? u.parentId?._id : u.parentId) || '');

/**
 * Builds the tree from the flat list the hierarchy endpoint returns (everyone
 * the viewer can see). A user whose manager isn't in that list — the viewer
 * themselves, or the workspace's top — is a root.
 */
function buildTree(users) {
  const byId = new Map(users.map(u => [String(u._id), u]));
  const children = new Map();
  const roots = [];
  for (const u of users) {
    const pid = parentOf(u);
    if (pid && byId.has(pid)) {
      if (!children.has(pid)) children.set(pid, []);
      children.get(pid).push(u);
    } else {
      roots.push(u);
    }
  }
  const order = (a, b) => (ROLE_RANK[a.role] ?? 9) - (ROLE_RANK[b.role] ?? 9) || fullName(a).localeCompare(fullName(b));
  roots.sort(order);
  for (const list of children.values()) list.sort(order);

  // Everyone below each user, counted by role
  const counts = new Map();
  const countBelow = (u) => {
    const id = String(u._id);
    if (counts.has(id)) return counts.get(id);
    const c = { total: 0 };
    for (const child of children.get(id) || []) {
      c.total += 1;
      c[child.role] = (c[child.role] || 0) + 1;
      const sub = countBelow(child);
      for (const [k, v] of Object.entries(sub)) c[k] = (c[k] || 0) + v;
    }
    counts.set(id, c);
    return c;
  };
  users.forEach(countBelow);

  return { byId, children, roots, counts };
}

const teamSummary = (c) => ['admin', 'dealer', 'sales_executive']
  .filter(r => c[r])
  .map(r => `${c[r]} ${c[r] === 1 ? ROLE_LABELS[r].toLowerCase() : ROLE_PLURAL[r]}`)
  .join(' · ');

function Node({ user, depth, tree, expanded, toggle, visible, matches, onShowTeam }) {
  const id   = String(user._id);
  const kids = (tree.children.get(id) || []).filter(k => !visible || visible.has(String(k._id)));
  const open = expanded.has(id);
  const c    = tree.counts.get(id) || { total: 0 };
  const hit  = matches?.has(id);

  return (
    <>
      <div
        className={`flex items-center gap-3 py-2 pr-4 border-b border-gray-50 ${hit ? 'bg-amber-50' : 'hover:bg-gray-50'}`}
        style={{ paddingLeft: `${16 + depth * 28}px` }}
      >
        <button
          type="button"
          onClick={() => toggle(id)}
          disabled={!kids.length}
          className={`h-6 w-6 flex items-center justify-center rounded ${kids.length ? 'text-gray-500 hover:bg-gray-200' : 'invisible'}`}
          aria-label={open ? 'Collapse' : 'Expand'}
        >
          {open ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
        </button>

        <div className="h-8 w-8 rounded-full bg-primary-600 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">
          {initials(user)}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-900">{fullName(user)}</span>
            <span className={`badge ${ROLE_BADGE[user.role] || 'badge-secondary'}`}>{ROLE_LABELS[user.role] || user.role}</span>
            {user.userCode && <span className="text-xs font-mono text-gray-500">{user.userCode}</span>}
            {user.status !== 'active' && <span className="badge bg-gray-100 text-gray-600 capitalize">{user.status}</span>}
          </div>
          <p className="text-xs text-gray-500 truncate">
            {[user.email, user.phone].filter(Boolean).join(' · ')}
          </p>
        </div>

        {c.total > 0 && (
          <div className="hidden sm:flex items-center gap-3 flex-shrink-0">
            <span className="text-xs text-gray-500">{teamSummary(c)}</span>
            <button
              type="button"
              onClick={() => onShowTeam(user)}
              className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:underline"
              title="Show this team in the list, with all its details"
            >
              <ListBulletIcon className="h-3.5 w-3.5" />
              List
            </button>
          </div>
        )}
      </div>

      {open && kids.map(k => (
        <Node key={k._id} user={k} depth={depth + 1} tree={tree} expanded={expanded} toggle={toggle}
          visible={visible} matches={matches} onShowTeam={onShowTeam} />
      ))}
    </>
  );
}

/**
 * Who reports to whom — the super admin's admins, each admin's dealers, each
 * dealer's sales executives — limited to what the viewer can see.
 * `onShowTeam(user)` opens that user's downline in the list view.
 */
export default function TeamTree({ onShowTeam }) {
  const { data, isLoading } = useQuery('hierarchyUsers', () => userService.getHierarchy(), { staleTime: 60 * 1000 });
  const users = useMemo(() => data?.data?.data?.users || data?.data?.users || [], [data]);
  const tree  = useMemo(() => buildTree(users), [users]);

  // Opens on the top level's direct reports; null until the viewer toggles
  const [expanded, setExpanded] = useState(null);
  const openSet = useMemo(
    () => expanded || new Set(tree.roots.map(r => String(r._id))),
    [expanded, tree]
  );
  const toggle = (id) => setExpanded(() => {
    const next = new Set(openSet);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const expandAll   = () => setExpanded(new Set([...tree.children.keys()]));
  const collapseAll = () => setExpanded(new Set());

  // Search: matching people, plus everyone above them so they stay in place
  const [search, setSearch] = useState('');
  const { visible, matches } = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return { visible: null, matches: null };
    const digits = q.replace(/\D/g, '');
    const hits = users.filter(u =>
      fullName(u).toLowerCase().includes(q)
      || (u.email || '').toLowerCase().includes(q)
      || (u.userCode || '').toLowerCase().includes(q)
      || (digits.length >= 3 && (u.phone || '').replace(/\D/g, '').includes(digits))
    );
    const vis = new Set();
    for (const u of hits) {
      let cur = u;
      while (cur && !vis.has(String(cur._id))) {
        vis.add(String(cur._id));
        cur = tree.byId.get(parentOf(cur));
      }
    }
    return { visible: vis, matches: new Set(hits.map(u => String(u._id))) };
  }, [search, users, tree]);
  // While searching, every path to a match is open
  const shownOpen = visible || openSet;

  if (isLoading) return <div className="p-8 text-center"><Spinner size="lg" /></div>;
  if (!users.length) return <div className="p-8 text-center text-sm text-gray-500">No team members yet.</div>;

  const roots = tree.roots.filter(r => !visible || visible.has(String(r._id)));

  return (
    <div>
      <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Find someone by name, email, mobile or user ID…"
            className="input text-sm w-full pl-9"
          />
        </div>
        {!visible && (
          <div className="flex gap-2 text-xs">
            <button type="button" onClick={expandAll} className="btn-outline-secondary px-3 py-1.5">Expand all</button>
            <button type="button" onClick={collapseAll} className="btn-outline-secondary px-3 py-1.5">Collapse all</button>
          </div>
        )}
        {visible && <span className="text-xs text-gray-500">{matches.size} match{matches.size === 1 ? '' : 'es'}</span>}
      </div>

      {!roots.length ? (
        <div className="p-8 text-center text-sm text-gray-500">No one matches &ldquo;{search}&rdquo;.</div>
      ) : (
        <div>
          {roots.map(r => (
            <Node key={r._id} user={r} depth={0} tree={tree} expanded={shownOpen}
              toggle={visible ? () => {} : toggle} visible={visible} matches={matches} onShowTeam={onShowTeam} />
          ))}
        </div>
      )}
    </div>
  );
}
